import React, { useEffect, useContext, useMemo } from "react";
import { Button, CardNotification, Text, ThemeStore, Weekly } from "@foris/avocado-suite";
import WeekTooltipLabel from "@components/week-tooltip-label";
import cx from "classnames";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { BookingContext } from "../../../../context/BookingContext";
import { Types } from "../../../../context/search.reducer";
import DayRow from "../../../../components/DayRow/DayRow";
import Requirements from "../../../../components/Requirements/Requirements";
import Preferences from "../../../../components/Preferences/Preferences";
import useGetOptions from "../../../../hooks/useGetOptions";
import useGetBuilding from "../../../../hooks/useGetBuilding";
import useGetClassroomType from "../../../../hooks/useGetClassroomType";
import useGetIntervals from "../../../../hooks/useIntervals";
import { getHasBlockOptions } from "@modules/booking/utils/blockOptions";
import uuid from "uuid-random";
import { formats, getFormattedDate } from "@utils/formats";
import { Holiday } from "@models/ISchema";
import { getIsCurrentWeek } from "@modules/booking/utils/classroomEvents";
import { useTranslation } from "react-i18next";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import css from "./recurrentEvent.module.scss";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
interface RecurrentEventProps {
  selectedHolidays?: Holiday[];
  hasNotUsableDatesSelected?: boolean;
  onCollission?: (hasCollision: boolean) => void;
}

const RecurrentEvent: React.FC<RecurrentEventProps> = ({
  onCollission,
  selectedHolidays,
  hasNotUsableDatesSelected,
}) => {
  const { state, dispatch } = useContext(BookingContext);
  const { scenario, isAdminBooking } = useBookingContextUrl();
  const [data, getBlocksOptions] = useGetOptions();
  const [building, getBuildingsOptions] = useGetBuilding();
  const [classroomTypes, getClassroomOptions] = useGetClassroomType();
  const [intervals, getIntervals] = useGetIntervals();
  const hasBlockOptions = getHasBlockOptions(state?.search?.blockOptions);
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const { t } = useTranslation();

  const handleGetClassroomOptions = (buildingId?: string) => {
    if (!classroomTypes?.loading) {
      getClassroomOptions(scenario, buildingId);
    }
  };

  /**
   * Get selected weeks sorted by `startingDate`
   */
  const selectedWeeks = useMemo(() => {
    const filteredWeeks = (state?.search?.intervals ?? []).filter(week => week.selected);
    const sortedWeeks = filteredWeeks.sort((a, b) => {
      const dateA = new Date(a?.startingDate)?.getTime();
      const dateB = new Date(b?.startingDate)?.getTime();
      return dateA - dateB;
    });

    return sortedWeeks;
  }, [state?.search?.intervals]);

  const weeklyItems = useMemo(() => {
    const changedWeeks = (weekId: string) => {
      const currentWeeks = [...state?.search?.intervals];
      const findIndex = currentWeeks?.findIndex(value => value?.id === weekId);
      currentWeeks[findIndex].selected = !currentWeeks[findIndex]?.selected;
      return currentWeeks;
    };

    const dataIntervals = state?.search?.intervals?.map(week => {
      const startDate = dayjs(week?.startingDate)?.format("DD.MM.YYYY");
      const endDate = dayjs(week?.endingDate)?.format("DD.MM.YYYY");
      const { isCurrentWeek, isBeforeCurrentWeek } = getIsCurrentWeek(week);
      const isNotUsable = !isAdminBooking && !week?.usableInBookings;
      const isDisabled = (!isCurrentWeek && isBeforeCurrentWeek) || isNotUsable;

      return {
        id: week?.id,
        label: week?.value,
        isDisabled,
        isSelected: week?.selected,
        tooltipLabel: isNotUsable ? (
          t("booking.booking-list-app.notifications.not-usable-date.weekly-tooltip")
        ) : (
          <WeekTooltipLabel
            weekLabel={`S${week.value}`}
            startingDate={startDate}
            endingDate={endDate}
          />
        ),
        onClick: () => {
          !isDisabled && dispatch({ type: Types.SetIntervals, payload: changedWeeks(week.id) });
        },
        className: isCurrentWeek ? "currentWeek" : "",
      };
    });

    return dataIntervals;
  }, [state?.search?.intervals]);

  useEffect(() => {
    if (!hasBlockOptions && !data?.data && !data?.loading) {
      getBlocksOptions(scenario);
    }

    if (state?.search?.buildingOptions?.length === 0 && !building?.data && !building?.loading) {
      getBuildingsOptions(scenario);
    }

    if (
      state?.search?.classroomTypesOptions?.length === 0 &&
      !classroomTypes?.data &&
      !classroomTypes?.loading
    ) {
      handleGetClassroomOptions();
    }

    if (!state?.search?.intervals?.length && !intervals?.data)
      getIntervals(scenario, undefined, false);

    return () => {
      onCollission?.(false);
    };
  }, []);

  const holidaysToRender = useMemo(() => {
    const holidays = [];

    selectedWeeks.forEach(selectedWeek => {
      const startDate = getFormattedDate(selectedWeek.startingDate);
      const endDate = getFormattedDate(selectedWeek.endingDate);

      selectedHolidays.forEach(holiday => {
        if (holiday?.date >= startDate && holiday?.date <= endDate) {
          holidays.push({
            ...holiday,
            week: selectedWeek?.value,
          });
        }
      });
    });

    return Object.values(holidays);
  }, [selectedHolidays]);

  return (
    <section className={css.recurrentEvent}>
      {hasBlockOptions && (
        <>
          <section className={css.recurrentEvent_dateRange}>
            {state?.search?.dateTime?.map((days, idx) => (
              <DayRow key={uuid()} index={days?.index ?? idx + 1} day="day" />
            ))}

            <Button
              size="sm"
              variant="ghost"
              leftIcon="circle-plus"
              onClick={() => {
                dispatch({
                  type: Types.SetDateTime,
                  payload: [
                    ...(state?.search?.dateTime ?? []),
                    {
                      index: state?.search?.dateTime?.length + 1,
                      recurrence: true,
                      day: {
                        value: null,
                        error: false,
                      },
                      start: {
                        value: null,
                        error: false,
                      },
                      end: {
                        value: null,
                        error: false,
                      },
                    },
                  ],
                });
              }}
            >
              {t("booking.booking-form.date-selector.add-day")}
            </Button>

            {!!state?.search?.intervals?.length && !!weeklyItems.length && (
              <>
                <Weekly
                  className={cx("editionWeekly", themeMode === "dark" && "editionWeekly__dark")}
                  weeklyItems={weeklyItems}
                />

                {selectedWeeks[0] && (
                  <section className={css.datesResumeWrapper}>
                    <div className={css.datesResumeWrapper_item}>
                      <Text className={css.itemLabel} type="sm">
                        {t("booking.booking-form.date-selector.week-row.start-week")}
                      </Text>

                      <Text className={css.itemValue}>
                        <span className={css.itemValue_week}>
                          {t("booking.booking-form.date-selector.week-row.week", {
                            week: selectedWeeks[0]?.value,
                          })}
                        </span>{" "}
                        {dayjs(selectedWeeks[0]?.startingDate).format("DD.MM.YYYY")}
                        {" - "}
                        {dayjs(selectedWeeks[0]?.endingDate).format("DD.MM.YYYY")}
                      </Text>
                    </div>

                    <div className={css.datesResumeWrapper_item}>
                      <Text className={css.itemLabel} type="sm">
                        {t("booking.booking-form.date-selector.week-row.end-week")}
                      </Text>

                      <Text className={css.itemValue}>
                        <span className={css.itemValue_week}>
                          {t("booking.booking-form.date-selector.week-row.week", {
                            week: selectedWeeks[selectedWeeks.length - 1]?.value,
                          })}
                        </span>{" "}
                        {dayjs(selectedWeeks[selectedWeeks.length - 1]?.startingDate).format(
                          "DD.MM.YYYY",
                        )}
                        {" - "}
                        {dayjs(selectedWeeks[selectedWeeks.length - 1]?.endingDate).format(
                          "DD.MM.YYYY",
                        )}
                      </Text>
                    </div>
                  </section>
                )}
              </>
            )}

            {!!holidaysToRender?.length && (
              <CardNotification
                className={cx(css.cardWarning, css.recurrentEvent_notificationCard)}
                title={t("booking.booking-form.notifications.holiday.title", {
                  count: selectedHolidays?.length ?? 0,
                })}
                state={isAdminBooking ? "warning" : "error"}
              >
                <>
                  {holidaysToRender?.map(holiday => (
                    <Text key={holiday?.code} className={css.holidayText}>
                      <b>
                        {t("booking.booking-form.date-selector.week-row.week", {
                          week: holiday?.week,
                        })}
                      </b>{" "}
                      {t("booking.booking-form.notifications.holiday.description", {
                        date: formats.dateEsFormat(holiday?.date),
                        name: holiday?.name,
                      })}
                    </Text>
                  ))}
                </>
              </CardNotification>
            )}

            {hasNotUsableDatesSelected && (
              <CardNotification
                className={cx(css.recurrentEvent_notificationCard)}
                title={t("booking.booking-list-app.notifications.not-usable-date.title")}
                state={isAdminBooking ? "warning" : "error"}
              >
                <Text type="sm">
                  {t("booking.booking-list-app.notifications.not-usable-date.description")}
                </Text>
              </CardNotification>
            )}
          </section>
          <Requirements />
          <Preferences
            isLoadingClassroomTypes={classroomTypes?.loading}
            onBuildingChange={handleGetClassroomOptions}
          />
        </>
      )}
    </section>
  );
};

export default RecurrentEvent;
